import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { CustomerEvent } from '../types';
import { emailIdsMissingBody } from './emailThreads';

/**
 * funeral_feed has no HTML column, so an email with no plain body gets its HTML
 * from communications. Returns the same events with body_html filled in.
 */
export function useEmailBodies<T extends CustomerEvent>(events: T[] | undefined): T[] | undefined {
  const ids = useMemo(() => emailIdsMissingBody(events), [events]);
  const { data: htmlById } = useQuery(
    ['funeral_feed_email_html', ids.join(',')],
    async () => {
      const { data, error } = await supabase.from('communications').select('id, body_html').in('id', ids);
      if (error) throw error;
      return new Map((data || []).map((r) => [String(r.id), r.body_html as string | null]));
    },
    { enabled: ids.length > 0, staleTime: 60 * 1000 },
  );
  return useMemo(
    () =>
      htmlById?.size
        ? events?.map((e) => (htmlById.has(String(e.id)) ? { ...e, body_html: htmlById.get(String(e.id)) } : e))
        : events,
    [events, htmlById],
  );
}
